/**
 * Touch image web component example.
 *
 * <touch-image image="url to image"></touch-image>
 *
 * @author Stephen Paul Hassall
 * @license https://CodeRunDebug.com/license.html
 * @web https://CodeRunDebug.com
 */

import TouchTools from "./touch-tools.js";

class TouchImage extends HTMLElement {
    /**
     * Constructor function.
     */
    constructor() {
        // Must call super first
        super();

        // Attach shadow DOM root
        this.attachShadow({mode: 'open'});

        // Set inner HTML of shadow DOM
        this.shadowRoot.innerHTML = `<canvas id="canvas"></canvas>`;

        // Get canvas element
        this._canvasElement = this.shadowRoot.getElementById('canvas');

        // Set styles
        this._canvasElement.style.width = '100%';
        this._canvasElement.style.height = '100%';

        // Set function binding
        this._imageLoadEvent = this._imageLoadEvent.bind(this);

        // Set no image
        this._image = null;

        // Set touch event function binding
        this._touchStartEvent = this._touchStartEvent.bind(this);
        this._touchMoveEvent = this._touchMoveEvent.bind(this);

        // Set pointer (non-touch device) event function binding
        this._pointerDownEvent = this._pointerDownEvent.bind(this);
        this._pointerUpEvent = this._pointerUpEvent.bind(this);
        this._pointerMoveEvent = this._pointerMoveEvent.bind(this);

        // Set touch tools
        this._touchTools = new TouchTools();

        // Set touch element
        this._touchTools.setTouchElement(this._canvasElement);
    }

    /**
    * Override connectedCallback function to handle when component is attached into the DOM.
    * @override
    */
    connectedCallback() {
        // Set the canvas element width and height to match client width and height
        this._canvasElement.width = this._canvasElement.clientWidth;
        this._canvasElement.height = this._canvasElement.clientHeight;

        // Get canvas context
        this._canvasContext = this._canvasElement.getContext('2d');

        // Add touch events
        this.addEventListener('touchstart', this._touchStartEvent, { passive: true });
        this.addEventListener('touchmove', this._touchMoveEvent, { passive: true });

        // Add pointer events
        this.addEventListener('pointerdown', this._pointerDownEvent, { passive: true });
        this.addEventListener('pointerup', this._pointerUpEvent, { passive: true });
        this.addEventListener('pointermove', this._pointerMoveEvent, { passive: true });
    }

    /**
     * Override disconnectedCallback function to handle when component is detached from the DOM.
     * @override
     */
    disconnectedCallback() {
        // Remove touch events
        this.removeEventListener('touchstart', this._touchStartEvent, { passive: true });
        this.removeEventListener('touchmove', this._touchMoveEvent, { passive: true });

        // Remove pointer events
        this.removeEventListener('pointerdown', this._pointerDownEvent, { passive: true });
        this.removeEventListener('pointerup', this._pointerUpEvent, { passive: true });
        this.removeEventListener('pointermove', this._pointerMoveEvent, { passive: true });
    }

    /**
     * Override attributeChangedCallback function to handle attribute changes
     * @param {string} name Then name of the attribute that has changed.
     * @param {string} oldValue The old value of the attribute before it was changed.
     * @param {string} newValue The new value the attribute is being changed to.
     * @override
     */
    attributeChangedCallback(name, oldValue, newValue) {
        // Load the new image
        this._loadImage();
    }

    /**
     * Override the observedAttributes function to return the list
     * of attributes to monitor.
     * @return {Array} List of attribute names.
     * @static
     * @override
     */
    static get observedAttributes() {
        // Return the list of attributes
        return ['image'];
    }

    /**
     * Load the image, set some starting values and draw into the canvas.
     */
    _loadImage() {
        // If the image does not exist
        if (this._image === null) {
            // Create image object
            this._image = new Image();

            // Add the event
            this._image.addEventListener('load', this._imageLoadEvent);
        }

        // Set image src to the file
        this._image.src = this.getAttribute('image');
    }

    /**
     * Image load event.
     */
    _imageLoadEvent() {
        // Set starting parts to show the whole image

        // Workout the width and height ratios
        let widthRatio = this._canvasElement.width / this._image.width;
        let heightRatio = this._canvasElement.height / this._image.height;

        // Set starting zoom
        let startingZoom = widthRatio;
        if (heightRatio < widthRatio) startingZoom = heightRatio;

        // Set starting coordinate space adjustments
        this._touchTools.setCoordinateSpace(0, 0, startingZoom, 0);

        // Draw image
        this._drawImage();
    }

    /**
     * Touch start event.
     * @param {object} event The touch start event data.
     */
    _touchStartEvent(event) {
        // Call touch start event with list of touches
        this._touchTools.touchStart(event.touches);
    }

    /**
     * Touch move event.
     * @param {object} event The touch move event data.
     */
    _touchMoveEvent(event) {
        // Call touch move event with list of touches
        this._touchTools.touchMove(event.touches);

        // Draw the image in the canvas (after the touch adjustments are made)
        this._drawImage();
    }

    /**
     * Pointer down event.
     * @param {Object} event The pointer down event data.
     */
    _pointerDownEvent(event) {
        // Call pointer down event
        this._touchTools.pointerDown(event);
    }

    /**
     * Pointer up event.
     * @param {Object} event The pointer up event data.
     */
    _pointerUpEvent(event) {
        // Call pointer up event
        this._touchTools.pointerUp(event);
    }

    /**
     * Pointer move event.
     * @param {Object} event The pointer move event data.
     */
    _pointerMoveEvent(event) {
        // Call pointer move event
        this._touchTools.pointerMove(event);

        // Draw the image in the canvas (after the touch adjustments are made)
        this._drawImage();
    }

    /**
     * Draw the image using the coordinate space adjustment information.
     */
    _drawImage() {
        // Clear the canvas to start drawing on it
        this._canvasContext.save();
        this._canvasContext.fillStyle = '#F1F1F1';
        this._canvasContext.fillRect(0, 0, this._canvasElement.width, this._canvasElement.height);

        // Move the canvas into the middle of the view space
        this._canvasContext.translate(this._canvasElement.width / 2, this._canvasElement.height / 2);

        // Scale, translate and rotate the canvas into the coordinate space
        this._canvasContext.scale(this._touchTools.coordinateSpaceScale, this._touchTools.coordinateSpaceScale);
        this._canvasContext.translate(this._touchTools.coordinateSpaceX, -this._touchTools.coordinateSpaceY);
        // this._canvasContext.rotate(this._touchTools.coordinateSpaceRotate);

        // Move center of image
        this._canvasContext.translate(this._image.width / -2, this._image.height / -2);

        // Draw the image
        this._canvasContext.drawImage(this._image, 0, 0);

        // End drawing
        this._canvasContext.restore();
    }
}

// Tell the browser about the new tag and the class it is linked to
customElements.define('touch-image', TouchImage);
